import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, QueryCommand, PutCommand, GetCommand, DeleteCommand, UpdateCommand, ScanCommand } from '@aws-sdk/lib-dynamodb';
import { autoTagCycleFields, batchTagCycleFields } from '../lib/utils/cycleTagging';
import { userService } from './unifiedUserService';
import { fetchAuthSession } from 'aws-amplify/auth';
import mockDataService from './mockDataService';

// Check if we should use mock data
const shouldUseMockData = () => {
  // Force mock data if emergency access is being used
  const forceMock = typeof window !== 'undefined' && localStorage.getItem('force_mock_data') === 'true';
  const envMock = process.env.REACT_APP_FORCE_MOCK_DATA === 'true';
  const devMock = process.env.NODE_ENV === 'development' && !process.env.REACT_APP_COGNITO_USER_POOL_ID;
  
  // Debug logging only in development
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 DEBUG shouldUseMockData:', {
      forceMock,
      envMock,
      devMock,
      result: forceMock || envMock || devMock
    });
  }
  
  return forceMock || envMock || devMock;
};

// Function to get DynamoDB client with Cognito credentials
const getDynamoClient = async () => {
  try {
    const session = await fetchAuthSession();
    
    if (!session.credentials) {
      throw new Error('No valid credentials found. User may not be authenticated.');
    }

    const client = new DynamoDBClient({
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
      credentials: {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
    });

    return DynamoDBDocumentClient.from(client);
  } catch (error) {
    throw error;
  }
};

const CEUS_TABLE = process.env.REACT_APP_DYNAMODB_CEUS_TABLE || 'asl-ceu-tracker-ceus';

export const ceuService = {
  // ADMIN FUNCTION: Reset all cycle data to correct 2021-2024 cycle
  resetAllCycleData: async (userId) => {
    try {
      
      const docClient = await getDynamoClient();
      
      // Get all CEUs for the user
      const command = new QueryCommand({
        TableName: CEUS_TABLE,
        KeyConditionExpression: 'userId = :userId',
        ExpressionAttributeValues: {
          ':userId': userId,
        },
      });

      const response = await docClient.send(command);
      
      if (response.Items && response.Items.length > 0) {
        const ceus = response.Items;
        
        // Reset all CEUs to the 2021-2024 cycle (Daniel's previous cycle) - DISABLED
        const resetPromises = ceus.map(async (ceu) => {
          const updatedCEU = {
            ...ceu,
            cycleId: 'cycle_2021_2024',
            cyclePeriod: '2021-2024',
            cycleStartDate: '2021-01-01',
            cycleEndDate: '2024-12-31',
            isCurrentCycle: false
          };
          
          const updateCommand = new PutCommand({
            TableName: CEUS_TABLE,
            Item: updatedCEU,
          });
          
          await (await getDynamoClient()).send(updateCommand);
          return updatedCEU;
        });
        
        await Promise.all(resetPromises);
        
        return { 
          success: true, 
          message: `Successfully reset ${ceus.length} CEUs to 2021-2024 cycle`,
          resetCount: ceus.length
        };
      } else {
        return { success: true, message: 'No CEUs found to reset', resetCount: 0 };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // ADMIN FUNCTION: Fix incorrectly tagged 2024-2027 CEUs
  fixIncorrectCycleTags: async (userId) => {
    try {
      
      const docClient = await getDynamoClient();
      
      // Get all CEUs for the user
      const command = new QueryCommand({
        TableName: CEUS_TABLE,
        KeyConditionExpression: 'userId = :userId',
        ExpressionAttributeValues: {
          ':userId': userId,
        },
      });

      const response = await docClient.send(command);
      
      if (response.Items && response.Items.length > 0) {
        const ceus = response.Items;
        const incorrectCEUs = ceus.filter(ceu => ceu.cyclePeriod === '2024-2027');
        
        
        if (incorrectCEUs.length > 0) {
          // Fix all incorrect CEUs to 2021-2024 cycle
          const fixPromises = incorrectCEUs.map(async (ceu) => {
            const updatedCEU = {
              ...ceu,
              cycleId: 'cycle_2021_2024',
              cyclePeriod: '2021-2024',
              cycleStartDate: '2021-01-01',
              cycleEndDate: '2024-12-31',
              isCurrentCycle: false
            };
            
            const updateCommand = new PutCommand({
              TableName: CEUS_TABLE,
              Item: updatedCEU,
            });
            
            await (await getDynamoClient()).send(updateCommand);
            return updatedCEU;
          });
          
          await Promise.all(fixPromises);
          
          return { 
            success: true, 
            message: `Successfully fixed ${incorrectCEUs.length} CEUs from 2024-2027 to 2021-2024`,
            fixedCount: incorrectCEUs.length
          };
        } else {
          return { success: true, message: 'No incorrect cycle tags found', fixedCount: 0 };
        }
      } else {
        return { success: true, message: 'No CEUs found', fixedCount: 0 };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  getUserCEUs: async (userIdentifier) => {
    // Use mock data if flag is set
    if (shouldUseMockData()) {
      return mockDataService.getCEUs(userIdentifier);
    }

    try {
      let userIds = [userIdentifier];
      
      // If identifier is an email, find all matching userIds
      if (userIdentifier.includes('@')) {
        const userResult = await userService.getUser(userIdentifier);
        if (userResult.success && userResult.data) {
          userIds = [userResult.data.userId];
          
          // Also check for other accounts with same email
          const docClient = await getDynamoClient();
          const scanResult = await docClient.send(new QueryCommand({
            TableName: process.env.REACT_APP_DYNAMODB_USERS_TABLE,
            IndexName: 'email-index', // If exists, otherwise use scan
            KeyConditionExpression: 'email = :email',
            ExpressionAttributeValues: { ':email': userIdentifier }
          }).catch(async () => {
            // Fallback to scan if no GSI
            return await docClient.send(new ScanCommand({
              TableName: process.env.REACT_APP_DYNAMODB_USERS_TABLE,
              FilterExpression: 'email = :email',
              ExpressionAttributeValues: { ':email': userIdentifier }
            }));
          }));
          
          if (scanResult.Items) {
            userIds = scanResult.Items.map(user => user.userId);
          }
        } else {
          return { success: true, data: [] };
        }
      }

      const docClient = await getDynamoClient();
      let allCeus = [];
      
      // Get CEUs for all matching userIds
      for (const userId of userIds) {
        const command = new QueryCommand({
          TableName: CEUS_TABLE,
          KeyConditionExpression: 'userId = :userId',
          ExpressionAttributeValues: { ':userId': userId },
          ScanIndexForward: false,
        });

        const response = await docClient.send(command);
        if (response.Items) {
          // Debug logging only in development
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 DEBUG DynamoDB query returned', response.Items.length, 'items for userId:', userId);
            const augustCEU = response.Items.find(ceu => ceu.date === '2025-08-27');
            if (augustCEU) {
              console.log('🔍 DEBUG DynamoDB August 27 CEU:', {
                ceuId: augustCEU.ceuId,
                isCurrentCycle: augustCEU.isCurrentCycle,
                title: augustCEU.title?.substring(0, 50),
                cycleStartDate: augustCEU.cycleStartDate,
                cycleEndDate: augustCEU.cycleEndDate
              });
            }
          }
          allCeus.push(...response.Items);
        }
      }
      
      // Sort by date descending
      allCeus.sort((a, b) => new Date(b.dateEarned || b.createdAt) - new Date(a.dateEarned || a.createdAt));
      
      // Apply cycle tagging to all CEUs with user profile
      let taggedCEUs = allCeus;
      if (userIds.length === 1) {
        // Get user profile for cycle tagging
        const userProfileResult = await userService.getUser(userIds[0]);
        if (userProfileResult.success && userProfileResult.data) {
          taggedCEUs = batchTagCycleFields(allCeus, userProfileResult.data);
        }
      }
      
      // DEBUG: Check August 27 CEU before returning (development only)
      if (process.env.NODE_ENV === 'development') {
        const augustCEU = taggedCEUs.find(ceu => ceu.date === '2025-08-27');
        if (augustCEU) {
          console.log('🔍 DEBUG getUserCEUs - August 27 CEU after tagging:', {
            ceuId: augustCEU.ceuId,
            isCurrentCycle: augustCEU.isCurrentCycle,
            date: augustCEU.date
          });
        }
      }
      
      return { success: true, data: taggedCEUs };
    } catch (error) {
      console.error('Error getting user CEUs:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a specific CEU by ID
  getCEU: async (userId, ceuId) => {
    try {
      const docClient = await getDynamoClient();
      
      const command = new GetCommand({
        TableName: CEUS_TABLE,
        Key: {
          userId: userId,
          ceuId: ceuId,
        },
      });

      const response = await docClient.send(command);
      
      if (response.Item) {
        // Apply cycle tagging with user profile
        const userProfileResult = await userService.getUser(userId);
        let taggedCEU = response.Item;
        if (userProfileResult.success && userProfileResult.data) {
          taggedCEU = autoTagCycleFields(response.Item, userProfileResult.data);
        }
        
        return { success: true, data: taggedCEU };
      } else {
        return { success: false, error: 'CEU not found' };
      }
    } catch (error) {
      console.error('❌ DynamoDB getCEU error:', error);
      return { success: false, error: error.message };
    }
  },

  // Save/Update a CEU record with automatic cycle tagging
  saveCEU: async (ceuData) => {
    try {
      const docClient = await getDynamoClient();
      
      // Generate CEU ID if not provided
      if (!ceuData.ceuId) {
        ceuData.ceuId = `ceu_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }

      // Get user profile for cycle information
      const userProfileResult = await userService.getUser(ceuData.userId);
      const userProfile = userProfileResult.success ? userProfileResult.data : null;

      // Ensure required fields
      let finalData = {
        userId: ceuData.userId,
        ceuId: ceuData.ceuId,
        title: ceuData.title || '',
        instructor: ceuData.instructor || '',
        organization: ceuData.organization || '',
        date: ceuData.date || '',
        ceuHours: parseFloat(ceuData.ceuHours) || 0,
        description: ceuData.description || '',
        category: ceuData.category || '',
        subCategory: ceuData.subCategory || '',
        certificateUrl: ceuData.certificateUrl || '',
        ridReceived: ceuData.ridReceived || false,
        beiReceived: ceuData.beiReceived || false,
        createdAt: ceuData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Auto-tag with cycle information
      if (userProfile && finalData.date) {
        finalData = autoTagCycleFields(finalData, userProfile);
      }

      const command = new PutCommand({
        TableName: CEUS_TABLE,
        Item: finalData,
      });

      await docClient.send(command);
            return { success: true, data: finalData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update specific fields of a CEU record
  updateCEU: async (userId, ceuId, updateData) => {
    try {
      const docClient = await getDynamoClient();
      
      // Build update expression dynamically
      const updateExpressions = [];
      const expressionAttributeNames = {};
      const expressionAttributeValues = {};
      
      Object.keys(updateData).forEach((key, index) => {
        const attrName = `#attr${index}`;
        const attrValue = `:val${index}`;
        updateExpressions.push(`${attrName} = ${attrValue}`);
        expressionAttributeNames[attrName] = key;
        expressionAttributeValues[attrValue] = updateData[key];
      });
      
      // Add updatedAt timestamp
      const updatedAtName = `#attr${Object.keys(updateData).length}`;
      const updatedAtValue = `:val${Object.keys(updateData).length}`;
      updateExpressions.push(`${updatedAtName} = ${updatedAtValue}`);
      expressionAttributeNames[updatedAtName] = 'updatedAt';
      expressionAttributeValues[updatedAtValue] = new Date().toISOString();
      
      const command = new UpdateCommand({
        TableName: CEUS_TABLE,
        Key: {
          userId: userId,
          ceuId: ceuId,
        },
        UpdateExpression: `SET ${updateExpressions.join(', ')}`,
        ExpressionAttributeNames: expressionAttributeNames,
        ExpressionAttributeValues: expressionAttributeValues,
        ReturnValues: 'ALL_NEW'
      });
      
      const result = await docClient.send(command);
      return { success: true, data: result.Attributes };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete a CEU record
  deleteCEU: async (userId, ceuId) => {
    try {
      const docClient = await getDynamoClient();
      
      const command = new DeleteCommand({
        TableName: CEUS_TABLE,
        Key: {
          userId: userId,
          ceuId: ceuId,
        },
      });

      await docClient.send(command);
            return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get CEUs for current cycle only (optimized query)
  getCurrentCycleCEUs: async (userId) => {
    try {
      // Get all CEUs first (since we don't have GSI for isCurrentCycle yet)
      const allCEUsResult = await ceuService.getCEUs(userId);
      if (!allCEUsResult.success) {
        return allCEUsResult;
      }
      
      // Filter for current cycle
      const currentCycleCEUs = allCEUsResult.data.filter(ceu => ceu.isCurrentCycle === true);
      
            return { success: true, data: currentCycleCEUs };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  },

  // Get CEUs for a specific cycle
  getCycleCEUs: async (userId, cycleId) => {
    try {
      // Get all CEUs first (since we don't have GSI for cycleId yet)
      const allCEUsResult = await ceuService.getCEUs(userId);
      if (!allCEUsResult.success) {
        return allCEUsResult;
      }
      
      // Filter for specific cycle
      const cycleCEUs = allCEUsResult.data.filter(ceu => ceu.cycleId === cycleId);
      
            return { success: true, data: cycleCEUs };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  },

  // Get previous cycle CEUs
  getPreviousCycleCEUs: async (userId) => {
    try {
      // Get all CEUs first
      const allCEUsResult = await ceuService.getCEUs(userId);
      if (!allCEUsResult.success) {
        return allCEUsResult;
      }
      
      // Filter for previous cycles (not current)
      const previousCycleCEUs = allCEUsResult.data.filter(ceu => ceu.isCurrentCycle === false);
      
            return { success: true, data: previousCycleCEUs };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  },

  // Batch update existing CEUs with cycle information (migration utility)
  batchUpdateCEUsWithCycles: async (userId) => {
    try {
            // Get all user CEUs
      const ceusResult = await ceuService.getCEUs(userId);
      if (!ceusResult.success) {
        throw new Error('Failed to fetch user CEUs');
      }
      
      // Get user profile
      const userProfileResult = await userService.getUser(userId);
      if (!userProfileResult.success) {
        throw new Error('Failed to fetch user profile');
      }
      
      const ceus = ceusResult.data;
      const userProfile = userProfileResult.data;
      
      // Tag all CEUs with cycle information
      const taggedCEUs = batchTagCycleFields(ceus, userProfile);
      
      // Update each CEU with cycle information
      const updatePromises = taggedCEUs.map(async (ceu) => {
        try {
          return await ceuService.saveCEU(ceu);
        } catch (error) {
          return { success: false, error: error.message };
        }
      });
      
      const results = await Promise.all(updatePromises);
      const successful = results.filter(r => r.success).length;
      
            return { success: true, data: { updated: successful, total: taggedCEUs.length } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Calculate CEU progress for a user
  calculateProgress: async (userId, certifyingBody = 'RID') => {
    try {
      const result = await ceuService.getCEUs(userId);
      
      if (!result.success) {
        return { success: false, error: result.error };
      }

      const ceus = result.data;
      
      // Calculate totals by category
      const professionalStudies = ceus
        .filter(ceu => ceu.category === 'Professional Studies')
        .reduce((sum, ceu) => sum + (ceu.ceuHours || 0), 0);
        
      const generalStudies = ceus
        .filter(ceu => ceu.category === 'General Studies')
        .reduce((sum, ceu) => sum + (ceu.ceuHours || 0), 0);

      const interpreting = ceus
        .filter(ceu => ceu.category === 'Interpreting')
        .reduce((sum, ceu) => sum + (ceu.ceuHours || 0), 0);

      const ethics = ceus
        .filter(ceu => ceu.category === 'Ethics')
        .reduce((sum, ceu) => sum + (ceu.ceuHours || 0), 0);

      const ppoHours = ceus
        .filter(ceu => ceu.subCategory === 'PPO')
        .reduce((sum, ceu) => sum + (ceu.ceuHours || 0), 0);

      const totalHours = professionalStudies + generalStudies + interpreting + ethics;

      // Calculate requirements based on certifying body
      let requirements = {};
      if (certifyingBody === 'RID') {
        requirements = {
          total: { required: 8.0, earned: totalHours },
          professionalStudies: { required: 6.0, earned: professionalStudies },
          generalStudies: { required: 0.0, earned: generalStudies, max: 2.0 },
          ppo: { required: 1.0, earned: ppoHours }
        };
      } else if (certifyingBody === 'BEI') {
        requirements = {
          total: { required: 10.0, earned: totalHours },
          interpreting: { required: 6.0, earned: interpreting },
          ethics: { required: 2.0, earned: ethics },
          generalStudies: { required: 0.0, earned: generalStudies, max: 2.0 }
        };
      }

      return {
        success: true,
        data: {
          totalCEUs: ceus.length,
          totalHours: totalHours,
          requirements: requirements,
          breakdown: {
            professionalStudies,
            generalStudies,
            interpreting,
            ethics,
            ppoHours
          }
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all CEUs for analytics (admin only)
   * Returns anonymized CEU data for system analytics
   */
  getAllCEUsForAnalytics: async () => {
    try {
      const client = await getDynamoClient();
      const tableName = process.env.REACT_APP_DYNAMODB_CEUS_TABLE;

      if (!tableName) {
        throw new Error('CEUs table name not configured');
      }

      const command = {
        TableName: tableName
      };

      // Use scan to get all CEUs across all users
      const { ScanCommand } = await import('@aws-sdk/lib-dynamodb');
      const scanCommand = new ScanCommand(command);
      const response = await client.send(scanCommand);

      if (!response.Items) {
        return { success: true, ceus: [] };
      }

      // Return only non-sensitive fields for analytics
      const analyticsCEUs = response.Items.map(ceu => ({
        userId: ceu.userId, // Keep for grouping, but no personal info
        ceuId: ceu.ceuId,
        category: ceu.category,
        subcategory: ceu.subcategory,
        ceuHours: ceu.ceuHours,  // ← Map ceuHours to hours for backward compatibility
        ceuHours: ceu.ceuHours,  // ← Also include the original field name
        title: ceu.title,
        createdAt: ceu.createdAt,
        updatedAt: ceu.updatedAt,
        certificateUrl: ceu.certificateUrl ? 'present' : 'absent',
        cycle: ceu.cycle
        // Exclude: presenter, organization, description, notes (personal/identifying info)
      }));

      return {
        success: true,
        ceus: analyticsCEUs
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

export default ceuService;
